// Agent Chat Context - Provides agent state to React components
// Decoupled from main sandbox state, connects via callbacks

import { useReducer, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { AgentSettings, CodeBlock, CodeCheckpoint } from './types';
import { agentReducer, initialAgentState, generateRequestId, generateCheckpointId } from './agentStore';
import { checkAgentConnection, sendAgentQuery, applyCodeToSandbox, revertToCheckpoint, cancelCurrentQuery, resetSessionId, postTestResults } from './agentApi';
import { AgentContext, type AgentContextValue, useAgent } from './agentContextCore';
import { createSpec as createDesignSpec, getSpec as getDesignSpec, setSpec as setDesignSpec, listSpecs } from '../design/designStore';
import { upsertRunbook, listRunbooks } from './runbookStore';
import { upsertGeneratedAsset } from '../assets/assetStore';
import type { TestAssetRef } from '@gotti/sandbox-core';
// The app's TestRunResult (engine result + the wall-clock `duration` this app
// measures). This callback used to declare its own inline result shape, which
// had drifted from what the only implementation — SandboxRunnerProvider's
// `runTestsFromFiles` — actually returns: it said `tests[].error` was a bare
// `string` and offered an `errorStack`, while the engine has shipped
// `error: { name, message, errorFrames? }` since the 2026-05-30 parity
// tightening, and it modelled neither `suites[].file`/`tests[].file` (which the
// fix planner keys every relatedFile off) nor `tests[].logs`.
import type { TestRunResult } from '../runner/types';
import { onFileChanged as kgOnFileChanged } from '@gotti/sandbox-ai-v2';
import { noteSyncedFile } from '../knowledge/syncFiles';
// onSpecChanged is wired via the designStore subscription in
// initKnowledgeGraph(); no inline call needed here.

export { AgentContext, useAgent };
export type { AgentContextValue };

// Callback type for applying code to the sandbox editor
export type ApplyCodeCallback = (filename: string, code: string) => void;

// Callback type for getting current sandbox files
export type GetSandboxFilesCallback = () => { path: string; content: string }[];

// Callback type for getting a specific file's content
export type GetFileContentCallback = (filename: string) => string | null;

// Callback type for running tests from flat file map (agent-delegated test execution)
export type RunTestsFromFilesCallback = (
  files: Record<string, string>,
  /** Run ONLY these spec files (closed inner loop subset); absent ⇒ all. */
  testFilePaths?: string[],
  /**
   * Generated assets the run must preload (overlaid onto the session asset
   * store by `createTestAssetSource`; refs WIN by path). Absent ⇒ session
   * store only — the worker omits the field when no assets were generated.
   */
  assets?: TestAssetRef[],
) => Promise<TestRunResult>;


interface AgentProviderProps {
  children: ReactNode;
  // Callbacks to interact with the sandbox (keeps agent decoupled)
  onApplyCode?: ApplyCodeCallback;
  getSandboxFiles?: GetSandboxFilesCallback;
  getFileContent?: GetFileContentCallback;
  onRunTestsFromFiles?: RunTestsFromFilesCallback;
  /** Authoritative full-snapshot of the AI's working files (REPLACE, not merge).
   *  Wired to the runner's `replaceAgentFiles` + editor reconcile so the file
   *  tree + TESTS panel always mirror exactly what the worker holds. */
  onSyncFiles?: (files: { path: string; content: string }[]) => void;
}

export function AgentProvider({
  children,
  onApplyCode,
  getSandboxFiles,
  getFileContent,
  onRunTestsFromFiles,
  onSyncFiles,
}: AgentProviderProps) {
  const [state, dispatch] = useReducer(agentReducer, initialAgentState);

  // Store callbacks in refs to avoid stale closures
  const onApplyCodeRef = useRef(onApplyCode);
  const getSandboxFilesRef = useRef(getSandboxFiles);
  const getFileContentRef = useRef(getFileContent);
  const onRunTestsFromFilesRef = useRef(onRunTestsFromFiles);
  const onSyncFilesRef = useRef(onSyncFiles);

  useEffect(() => {
    onApplyCodeRef.current = onApplyCode;
    getSandboxFilesRef.current = getSandboxFiles;
    getFileContentRef.current = getFileContent;
    onRunTestsFromFilesRef.current = onRunTestsFromFiles;
    onSyncFilesRef.current = onSyncFiles;
  }, [onApplyCode, getSandboxFiles, getFileContent, onRunTestsFromFiles, onSyncFiles]);
  
  // Check connection on mount; cancel in-flight queries on unmount
  useEffect(() => {
    checkAgentConnection()
      .then(connected => {
        dispatch({ type: 'SET_CONNECTED', payload: connected });
        if (connected) {
          dispatch({
            type: 'ADD_SYSTEM_MESSAGE',
            payload: { content: 'Agent connected. Ask me anything about your sandbox!' },
          });
        }
      })
      .catch(error => {
        dispatch({ type: 'SET_CONNECTION_ERROR', payload: error.message });
      });

    // Cleanup: abort any in-flight SSE stream when the provider unmounts
    return () => {
      cancelCurrentQuery();
    };
  }, []);
  
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || state.isProcessing) return;
    
    // Generate request ID before dispatching so reducer and event handlers share it
    const requestId = generateRequestId();
    dispatch({ type: 'SEND_MESSAGE', payload: { content, requestId } });
    
    // Get current sandbox context
    const sandboxContext = getSandboxFilesRef.current
      ? {
          files: getSandboxFilesRef.current(),
          designSpecs: listSpecs().map((s) => ({
            id: s.id,
            title: s.title,
            category: s.category,
            systemName: s.systemName,
            body: s.body,
            relatedCodeSymbols: s.relatedCodeSymbols,
            relatedSpecIds: s.relatedSpecIds,
          })),
          runbooks: listRunbooks(),
        }
      : undefined;
    
    // Send to API with streaming callbacks.
    // Wrapped in try/finally so isProcessing is ALWAYS reset, even if
    // sendAgentQuery throws before emitting any events (e.g. network error,
    // JSON parse failure, AbortError).
    try {
      await sendAgentQuery({
        query: content,
        settings: state.settings,
        sandboxContext,
        onEvent: (event) => {
          switch (event.type) {
            case 'status':
              dispatch({
                type: 'PIPELINE_STATUS',
                payload: {
                  requestId,
                  status: {
                    step: event.data.step,
                    message: event.data.message,
                    tool: event.data.tool,
                    detail: event.data.detail,
                  },
                },
              });
              break;
            case 'context':
              dispatch({
                type: 'CONTEXT_RETRIEVED',
                payload: { requestId, context: event.data },
              });
              break;
            case 'chunk':
              dispatch({
                type: 'STREAM_CHUNK',
                payload: { requestId, chunk: event.data },
              });
              break;
            case 'file_modified': {
              const modifiedPath = event.data.path;
              const modifiedContent = event.data.content;
              dispatch({
                type: 'FILE_MODIFIED',
                payload: { requestId, path: modifiedPath, content: modifiedContent },
              });

              // Auto-apply file content to the sandbox editor.
              // Tool-driven file writes are always applied (with undo via checkpoints).
              // This is distinct from autoApplyCode which controls markdown code blocks.
              if (modifiedContent && onApplyCodeRef.current) {
                try {
                  // Snapshot original content for checkpoint (undo support)
                  const getContent = getFileContentRef.current || (() => null);
                  const originalContent = getContent(modifiedPath);
                  const checkpointId = generateCheckpointId();
                  const checkpoint: CodeCheckpoint = {
                    id: checkpointId,
                    createdAt: Date.now(),
                    description: `AI modified ${modifiedPath}`,
                    codeBlockId: `auto-${requestId}-${modifiedPath}`,
                    filename: modifiedPath,
                    originalContent: originalContent || '',
                    reverted: false,
                  };
                  dispatch({ type: 'CREATE_CHECKPOINT', payload: { checkpoint } });

                  // Apply the new content to the editor
                  onApplyCodeRef.current(modifiedPath, modifiedContent);
                  console.log(`[Agent] Auto-applied ${modifiedPath} (checkpoint ${checkpointId})`);
                } catch (err) {
                  console.error(`[Agent] Failed to auto-apply ${modifiedPath}:`, err);
                }
              }
              // Knowledge graph: index this file (whether the editor
              // accepted it or not — KG tracks the AI's intent too).
              if (modifiedContent != null) {
                try {
                  const normalized = modifiedPath.startsWith('/') ? modifiedPath.slice(1) : modifiedPath;
                  kgOnFileChanged(normalized, modifiedContent);
                  noteSyncedFile(normalized, modifiedContent); // keep the pull-diff mirror coherent
                } catch (err) {
                  console.error('[KG] file index failed for', modifiedPath, err);
                }
              }
              break;
            }
            case 'files_sync': {
              // AUTHORITATIVE full snapshot of the AI's working files. Reconcile
              // the editor tree + runner mirror to EXACTLY this set so the file
              // tree + TESTS panel always reflect what the worker holds — covers
              // silent bulk mutations (rollback/autofix/bootstrap-strip) and
              // deletes that the per-file file_modified stream can't express.
              const files = event.data.files;
              try {
                onSyncFilesRef.current?.(files);
              } catch (err) {
                console.error('[Agent] files_sync reconcile failed:', err);
              }
              // Keep the knowledge-graph + pull-diff mirror coherent with the
              // authoritative set (the AI's intent), same as file_modified does.
              for (const f of files) {
                try {
                  const normalized = f.path.startsWith('/') ? f.path.slice(1) : f.path;
                  kgOnFileChanged(normalized, f.content);
                  noteSyncedFile(normalized, f.content);
                } catch (err) {
                  console.error('[KG] files_sync index failed for', f.path, err);
                }
              }
              break;
            }
            case 'complete':
              dispatch({
                type: 'STREAM_COMPLETE',
                payload: { requestId, codeBlocks: event.data.codeBlocks },
              });
              break;
            case 'error': {
              // When per-item error strings are present (e.g. the
              // "Execution failed with N error(s)" summary from the
              // ReAct loop), join them into the bubble so the user
              // sees the actual diagnostics instead of just a count.
              // Backend caps the array at 20 entries so this doesn't
              // overflow the chat panel.
              const detail =
                event.data.errors && event.data.errors.length > 0
                  ? `${event.data.message}\n\n${event.data.errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`
                  : event.data.message;
              dispatch({
                type: 'REQUEST_ERROR',
                payload: { requestId, error: detail },
              });
              break;
            }
            case 'test_progress':
              dispatch({
                type: 'TEST_PROGRESS',
                payload: { requestId, ...event.data },
              });
              break;
            case 'test_request': {
              // Agent delegated test execution to client browser
              const { sessionId, files, testFilePaths, assets } = event.data;
              const runner = onRunTestsFromFilesRef.current;
              if (!runner) {
                console.warn('[Agent] test_request received but no onRunTestsFromFiles callback');
                postTestResults(sessionId, {
                  passed: false, totalTests: 0, testsPassed: 0, testsFailed: 0, duration: 0,
                  fileResults: [{ file: 'unknown', passed: false, tests: 0, failures: 1, errors: ['No test runner available on client'] }],
                }).catch(e => console.error('[Agent] Failed to post test results:', e));
                break;
              }
              // Run tests asynchronously — don't block the event loop
              (async () => {
                try {
                  const result = await runner(files, testFilePaths, assets);
                  // Flat per-test failure list for the v2 fix-phase
                  // planner. Each entry pins a specific failed test
                  // back to its file so the planner can decide which
                  // source file to rewrite. `errorStack` carries the
                  // file:line refs the planner uses to localise the bug.
                  // Engine TestResult.error is { name, message, errorFrames? }
                  // (no longer a bare string, per the 2026-05-30 parity-engine
                  // tightening). Flatten to the v2 wire shape: error=string,
                  // errorFrames=string[].
                  const failures = result.suites.flatMap(s =>
                    s.tests
                      .filter(t => t.status === 'failed' || t.status === 'error')
                      .map(t => ({
                        // `file` MUST be the real .spec.ts PATH, not the
                        // describe() name — the fix planner keys relatedFiles /
                        // seedFiles / the failing-test source excerpt off it, so
                        // a suite-name here collapses all of them to empty and
                        // the diagnose+fix agents debug BLIND (never see the
                        // failing test's body). SuiteResult.file is populated by
                        // setCurrentTestFile() in the runner.
                        file: t.file ?? s.file ?? s.name,
                        suite: s.name,
                        testName: t.name,
                        error: t.error?.message || `${t.name} ${t.status}`,
                        errorFrames: t.error?.errorFrames,
                        // What the generated code logged during this test —
                        // runtime trace for the fix planner.
                        logs: t.logs,
                      })),
                  );
                  await postTestResults(sessionId, {
                    passed: result.totalFailed === 0 && result.totalTests > 0,
                    totalTests: result.totalTests,
                    testsPassed: result.totalPassed,
                    testsFailed: result.totalFailed,
                    duration: result.duration,
                    fileResults: result.suites.map(s => ({
                      file: s.file ?? s.name,
                      passed: s.failed === 0,
                      tests: s.tests.length,
                      failures: s.failed,
                      errors: s.tests
                        .filter(t => t.status === 'failed' || t.status === 'error')
                        .map(t => t.error?.message || `${t.name} failed`),
                    })),
                    failures,
                    // `result.coverage` is already the bounded wire shape
                    // (percentage + per-file `uncoveredLines`) — `runner()`
                    // (SandboxRunnerProvider's `runTestsFromFiles`) trims the
                    // full in-browser `CoverageReport` (per-line hit arrays)
                    // down before returning it. Forward it verbatim so the
                    // deep agent's `get_coverage` tool (graph.ts) can name the
                    // exact uncovered lines, not just an aggregate percentage.
                    coverage: result.coverage
                      ? {
                          percentage: result.coverage.percentage,
                          files: result.coverage.files.map(f => ({
                            path: f.path,
                            percentage: f.percentage,
                            uncoveredLines: f.uncoveredLines,
                          })),
                        }
                      : undefined,
                  });
                } catch (err) {
                  console.error('[Agent] Client test execution failed:', err);
                  await postTestResults(sessionId, {
                    passed: false, totalTests: 0, testsPassed: 0, testsFailed: 0, duration: 0,
                    fileResults: [{ file: 'unknown', passed: false, tests: 0, failures: 1, errors: [String(err)] }],
                  }).catch(e => console.error('[Agent] Failed to post test results:', e));
                }
              })();
              break;
            }
            // ── v2 recursive-tree pipeline ─────────────────────────────────
            case 'v2:workflow:start':
              dispatch({
                type: 'V2_WORKFLOW_START',
                payload: { requestId, rootId: event.data.rootId },
              });
              break;
            case 'v2:workflow:end':
              dispatch({ type: 'V2_WORKFLOW_END', payload: { requestId } });
              break;
            case 'v2:node:created':
              dispatch({
                type: 'V2_NODE_CREATED',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:status':
              dispatch({
                type: 'V2_NODE_STATUS',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:file':
              dispatch({
                type: 'V2_NODE_FILE',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:error':
              dispatch({
                type: 'V2_NODE_ERROR',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:chunk':
              dispatch({
                type: 'V2_NODE_CHUNK',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:activity':
              dispatch({
                type: 'V2_NODE_ACTIVITY',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:tool':
              dispatch({
                type: 'V2_NODE_TOOL',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:tokens':
              dispatch({
                type: 'V2_NODE_TOKENS',
                payload: { requestId, ...event.data },
              });
              break;
            case 'v2:node:designSpec': {
              // Twin write: (1) update the tree node so the UI shows
              // the spec was produced; (2) push the spec into the
              // sandbox's DesignDocs panel via designStore.
              dispatch({
                type: 'V2_NODE_DESIGN_SPEC',
                payload: { requestId, ...event.data },
              });
              try {
                const { spec } = event.data;
                const baseInput = {
                  title: spec.title,
                  category: spec.category,
                  systemName: spec.systemName,
                  body: spec.body,
                  relatedCodeSymbols: spec.relatedCodeSymbols ?? [],
                  relatedSpecIds: spec.relatedSpecIds ?? [],
                };
                if (spec.id && getDesignSpec(spec.id)) {
                  // Existing spec — update in place.
                  setDesignSpec({
                    ...baseInput,
                    id: spec.id,
                    createdAt: getDesignSpec(spec.id)?.createdAt ?? new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  });
                } else {
                  createDesignSpec(baseInput);
                }
              } catch (err) {
                console.error('[Agent] Failed to write design spec to store:', err);
              }
              break;
            }
            case 'v2:node:runbook': {
              // Fold the committed/retired/revived runbook into the in-memory
              // store; the next save persists it via extensions.gotti_runbooks.
              try {
                upsertRunbook(event.data.runbook);
              } catch (err) {
                console.warn('[AgentContext] runbook upsert failed', err);
              }
              break;
            }
            // ── v2 asset generation ────────────────────────────────────────
            // Minimal v1 surfacing: spec/created/failed append to the owning
            // tree node's activity feed (the same surface v2:node:activity
            // writes to; the reducer drops unknown ids gracefully) — no
            // dedicated panels. `created` additionally folds the asset into
            // the session asset store, which the knowledge graph re-indexes
            // via its existing store subscription.
            case 'v2:asset:spec':
              dispatch({
                type: 'V2_NODE_ACTIVITY',
                payload: {
                  requestId,
                  id: event.data.id,
                  message: `asset spec: ${event.data.spec.name} (${event.data.spec.composite}, ${event.data.spec.parts.length} part${event.data.spec.parts.length !== 1 ? 's' : ''})`,
                },
              });
              break;
            case 'v2:asset:created': {
              const asset = event.data.asset;
              dispatch({
                type: 'V2_NODE_ACTIVITY',
                payload: { requestId, id: event.data.id, message: `asset created: ${asset.path}` },
              });
              // Async (may fetch by hash/url when base64 isn't inlined);
              // failure is logged, never thrown — the stream keeps flowing.
              upsertGeneratedAsset(asset).catch((err) => {
                console.error('[Agent] Failed to fold generated asset into store:', asset.path, err);
              });
              break;
            }
            case 'v2:asset:failed':
              // Deliberately NOT V2_NODE_ERROR: one failed asset must not
              // flip the whole node to 'failed' while the worker continues
              // with the remaining specs.
              console.warn('[Agent] Asset generation failed:', event.data.path, '—', event.data.message);
              dispatch({
                type: 'V2_NODE_ACTIVITY',
                payload: {
                  requestId,
                  id: event.data.id,
                  message: `asset failed: ${event.data.path} — ${event.data.message}`,
                },
              });
              break;
          }
        },
      });
    } catch (error) {
      // Catch-all: if sendAgentQuery throws without emitting an 'error' event,
      // dispatch REQUEST_ERROR so the UI shows the failure and isProcessing resets.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      dispatch({
        type: 'REQUEST_ERROR',
        payload: { requestId, error: errorMessage },
      });
    }
  }, [state.isProcessing, state.settings]);
  
  const clearHistory = useCallback(() => {
    cancelCurrentQuery();   // abort any in-flight request
    resetSessionId();       // clear module-level session so backend starts fresh
    dispatch({ type: 'CLEAR_HISTORY' });
  }, []);
  
  const applyCode = useCallback(async (codeBlock: CodeBlock) => {
    if (!codeBlock.filename) {
      console.error('[Agent] Cannot apply code block without filename');
      return;
    }
    
    dispatch({ type: 'APPLY_CODE', payload: { codeBlockId: codeBlock.id } });
    
    // Get file content callback for creating checkpoint
    const getContent = getFileContentRef.current || (() => null);
    
    const result = await applyCodeToSandbox(
      codeBlock,
      (filename, code) => {
        onApplyCodeRef.current?.(filename, code);
      },
      getContent
    );
    
    if (result.success) {
      // Create a checkpoint before marking as applied
      const checkpointId = generateCheckpointId();
      const checkpoint: CodeCheckpoint = {
        id: checkpointId,
        createdAt: Date.now(),
        description: `Applied code to ${codeBlock.filename}`,
        codeBlockId: codeBlock.id,
        filename: codeBlock.filename,
        originalContent: result.originalContent || '',
        reverted: false,
      };
      
      dispatch({ type: 'CREATE_CHECKPOINT', payload: { checkpoint } });
      dispatch({ type: 'CODE_APPLIED', payload: { codeBlockId: codeBlock.id, checkpointId } });
      
      console.log(`[Agent] Created checkpoint ${checkpointId} for ${codeBlock.filename}`);
    }
  }, []);
  
  const revertCheckpointAction = useCallback(async (checkpoint: CodeCheckpoint) => {
    if (checkpoint.reverted) {
      console.warn('[Agent] Checkpoint already reverted:', checkpoint.id);
      return;
    }
    
    dispatch({ type: 'REVERT_CHECKPOINT', payload: { checkpointId: checkpoint.id } });
    
    const success = await revertToCheckpoint(
      checkpoint.filename,
      checkpoint.originalContent,
      (filename, code) => {
        onApplyCodeRef.current?.(filename, code);
      }
    );
    
    if (success) {
      dispatch({ type: 'CHECKPOINT_REVERTED', payload: { checkpointId: checkpoint.id } });
      dispatch({
        type: 'ADD_SYSTEM_MESSAGE',
        payload: { content: `Reverted changes to ${checkpoint.filename}` },
      });
      console.log(`[Agent] Reverted checkpoint ${checkpoint.id}`);
    }
  }, []);
  
  const updateSettings = useCallback((settings: Partial<AgentSettings>) => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
  }, []);
  
  const checkConnection = useCallback(async () => {
    try {
      const connected = await checkAgentConnection();
      dispatch({ type: 'SET_CONNECTED', payload: connected });
    } catch (error) {
      dispatch({
        type: 'SET_CONNECTION_ERROR',
        payload: error instanceof Error ? error.message : 'Connection failed',
      });
    }
  }, []);
  
  const value: AgentContextValue = {
    state,
    dispatch,
    sendMessage,
    clearHistory,
    applyCode,
    revertCheckpoint: revertCheckpointAction,
    updateSettings,
    checkConnection,
  };
  
  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
}

